/* Shell hooks — the behaviours that lived loose in js/app.js's init(). */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { qk } from '../lib/queryKeys'
import { useAuth } from '../auth/AuthContext'
import * as inboxApi from '../api/inbox'
import * as candidatesApi from '../api/candidates'
import * as tasksApi from '../api/tasks'
import * as jobsApi from '../api/jobs'
import * as notificationsApi from '../api/notifications'
import * as approvalsApi from '../api/approvals'

const SIDEBAR_KEY = 'tf-sidebar'

/** document.title and <html data-view> — CSS keys off data-view (js/app.js:38). */
export function useRouteMeta(route) {
  useEffect(() => {
    if (!route) return undefined
    document.documentElement.setAttribute('data-view', route.path)
    document.title = `TalentFlow · ${route.title}`
    return () => document.documentElement.removeAttribute('data-view')
  }, [route])
}

/**
 * Mobile nav drawer. All four class toggles are load-bearing for the frozen CSS:
 * `nav-open` on <html> is what the rules key off, because the AI FAB sits before
 * .scrim in the DOM so no sibling selector can reach it.
 */
export function useNavOpen() {
  const [navOpen, setNavOpen] = useState(false)

  useEffect(() => {
    document.documentElement.classList.toggle('nav-open', navOpen)
    document.body.style.overflow = navOpen ? 'hidden' : ''
    return () => {
      document.documentElement.classList.remove('nav-open')
    }
  }, [navOpen])

  // The drawer only exists ≤900px (styles.css). If the window grows past the
  // breakpoint while it is open, nav-open would strand the scrim over the
  // desktop layout with body scroll locked — close it instead.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = (e) => { if (!e.matches) setNavOpen(false) }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return [navOpen, setNavOpen]
}

export function useSidebarCollapsed() {
  // The prototype didn't persist this; a collapsed sidebar springing back open
  // on every navigation reads as a bug.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1'
    } catch {
      return false
    }
  })
  const toggle = useCallback(() => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1')
      } catch {
        /* ignore */
      }
      return !c
    })
  }, [])
  return [collapsed, toggle]
}

/** Cmd/Ctrl+K focuses search; Escape closes the drawer and the dock (js/app.js:297-300). */
export function useHotkeys({ onEscape }) {
  const searchRef = useRef(null)
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        searchRef.current?.focus()
      }
      if (e.key === 'Escape') onEscape?.()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onEscape])
  return searchRef
}

/**
 * The sidebar badge counts. App.updateBadges() was an imperative DOM write
 * that every mutating call site had to remember to call; these are derived, so
 * completing a task updates the badge with no call site involved at all.
 *
 * `matching` is the unassigned CV-bank queue — CVs imported with No job that
 * still have no job_post_id. Job Matching exists to drive that number to zero.
 */
export function useBadges() {
  const { can } = useAuth()
  const { data: matchingTotal = 0 } = useQuery({
    queryKey: qk.candidates.matching({ assigned: false, count: true }),
    meta: { globalLoader: false }, // background — never blur the screen
    queryFn: async () => {
      try {
        const res = await candidatesApi.listMatching({ assigned: false, top: 1 })
        return res?.total ?? 0
      } catch {
        return 0
      }
    },
  })
  const { data: tasksTotal = 0 } = useQuery({
    queryKey: qk.tasks.list({ badge: 'open' }),
    meta: { globalLoader: false }, // background — never blur the screen
    queryFn: async () => {
      try {
        const res = await tasksApi.list({ status: 'open', top: 1 })
        return res?.total ?? 0
      } catch {
        return 0
      }
    },
  })
  const { data: jobsOpen = 0 } = useQuery({
    queryKey: qk.jobs.list({ badge: 'open' }),
    meta: { globalLoader: false }, // background — never blur the screen
    queryFn: async () => {
      try {
        const res = await jobsApi.list({ requisitionStatus: 'open', top: 1 })
        return res?.total ?? 0
      } catch {
        return 0
      }
    },
  })
  // Shares its cache entry with the Inbox tabs, so it must fetch the same shape
  // and narrow with `select` — see inboxApi.fetchCounts.
  const { data: inboxUnread = 0 } = useQuery({
    queryKey: qk.mailbox.counts(),
    meta: { globalLoader: false }, // background — never blur the screen
    queryFn: inboxApi.fetchCounts,
    select: (data) => data?.unread ?? 0,
  })
  // Same key and fetcher as the Topbar bell: one request per poll serves both.
  const { data: notifUnread = 0 } = useQuery({
    queryKey: qk.notifications.list({ top: 6 }),
    queryFn: notificationsApi.fetchFeed,
    select: (data) => data?.unread ?? 0,
    staleTime: 0,
    refetchInterval: 15_000,
  })

  // Approvals: requests whose current step the signed-in user can decide.
  const { data: approvalsPending = 0 } = useQuery({
    queryKey: qk.approvals.count(),
    queryFn: approvalsApi.pendingCount,
    enabled: can('requisitions.approve') || can('offers.approve'),
    refetchInterval: 60_000,
  })

  return {
    jobs: jobsOpen,
    approvals: approvalsPending,
    notifications: notifUnread,
    tasks: tasksTotal,
    inbox: inboxUnread,
    matching: matchingTotal,
  }
}
